<template>
  <div class="pa-root">

    <!-- Not authorized -->
    <div v-if="!authorized" class="pa-center">
      <div class="pa-lock-card">
        <div style="font-size:48px">🔒</div>
        <h2>Accès réservé</h2>
        <p>Cette page est réservée à l'administrateur de la plateforme.</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="pa-center">
      <div class="pa-spinner"></div>
    </div>

    <!-- Editor -->
    <div v-else>
      <div class="pa-header">
        <div class="pa-title">⚙️ Gestion des plans</div>
        <div class="pa-header-actions">
          <a href="/pricing" target="_blank" class="pa-btn-preview">👁 Voir la page pricing</a>
          <button class="pa-btn-save" @click="saveAll" :disabled="saving">
            {{ saving ? '⏳ Sauvegarde…' : '💾 Sauvegarder' }}
          </button>
        </div>
      </div>

      <div class="pa-hint">
        Les modifications sont sauvegardées dans Firebase et mises à jour instantanément sur la page pricing.
        Pour ajouter une nouvelle feature ou catégorie, modifie <code>src/plans.js</code>.
      </div>

      <!-- Video hint -->
      <div class="pa-info-box">
        📹 <strong>Vidéo :</strong> les créateurs insèrent une URL YouTube (ex: <code>https://youtube.com/watch?v=…</code>).
        La vidéo est intégrée en iframe. Pas d'upload nécessaire — YouTube gère l'hébergement.
      </div>

      <div class="pa-grid">
        <div v-for="planKey in planKeys" :key="planKey" class="pa-plan-col">

          <!-- Plan header -->
          <div class="pa-plan-header" :class="'accent-' + planKey">
            <div class="pa-plan-icon-row">
              <input class="pa-plan-icon-input" v-model="plans[planKey].icon"
                placeholder="🪶" maxlength="2" title="Emoji" />
              <input class="pa-plan-name-input" v-model="plans[planKey].label"
                :placeholder="planKey" />
            </div>
            <div class="pa-plan-key">{{ planKey }}</div>
          </div>

          <!-- Price & limits -->
          <div class="pa-section">
            <div class="pa-section-title">Général</div>

            <div class="pa-field">
              <label class="pa-label">Prix (ILS / mois)</label>
              <div class="pa-price-wrap">
                <input type="number" min="0" v-model.number="plans[planKey].price"
                  class="pa-input" />
                <span class="pa-currency">₪</span>
              </div>
              <div v-if="plans[planKey].price === 0" class="pa-badge-free">Gratuit</div>
            </div>

            <div class="pa-field">
              <label class="pa-label">Max parcours</label>
              <div class="pa-input-row">
                <input type="number" min="1" v-model.number="plans[planKey].maxGames"
                  class="pa-input" />
                <span v-if="plans[planKey].maxGames >= 999" class="pa-badge-unlimited">Illimité</span>
              </div>
            </div>

            <div class="pa-field">
              <label class="pa-label">Max points par parcours</label>
              <div class="pa-input-row">
                <input type="number" min="1" v-model.number="plans[planKey].maxPoints"
                  class="pa-input" />
                <span v-if="plans[planKey].maxPoints >= 999" class="pa-badge-unlimited">Illimité</span>
              </div>
            </div>
          </div>

          <!-- Feature categories -->
          <div v-for="cat in featureCategories" :key="cat.key" class="pa-section">
            <div class="pa-section-title">{{ cat.labels.fr }}</div>
            <div v-for="feat in cat.features" :key="feat.key" class="pa-checkbox-row"
              @click="toggle(planKey, feat.key)">
              <div class="pa-checkbox" :class="{ checked: plans[planKey].features[feat.key] }">
                <span v-if="plans[planKey].features[feat.key]">✓</span>
              </div>
              <span class="pa-feat-label">{{ feat.labels.fr }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Toast -->
      <div v-if="toast" class="pa-toast">✅ Plans sauvegardés dans Firebase !</div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import { PLAN_KEYS, FEATURE_CATEGORIES, DEFAULT_PLANS } from '../plans.js';

const SUPER_ADMINS = ['ephraimichael@gmail.com', 'eifoany@gmail.com'];

export default {
  data() {
    return {
      authorized: false,
      loading: true,
      saving: false,
      toast: false,
      plans: {},
      planKeys: PLAN_KEYS,
      featureCategories: FEATURE_CATEGORIES,
    };
  },

  async mounted() {
    firebase.auth().onAuthStateChanged(async user => {
      if (!user || !SUPER_ADMINS.includes(user.email)) {
        this.loading = false;
        this.authorized = false;
        return;
      }
      this.authorized = true;
      await this.loadPlans();
      this.loading = false;
    });
  },

  methods: {
    async loadPlans() {
      const db = firebase.database();
      const snap = await db.ref('/plans').once('value');
      const stored = snap.val() || {};

      // Merge stored values with defaults so new features appear automatically
      const merged = {};
      for (const key of PLAN_KEYS) {
        const def = JSON.parse(JSON.stringify(DEFAULT_PLANS[key]));
        const src = stored[key] || {};
        merged[key] = {
          label:     src.label     ?? def.label,
          icon:      src.icon      ?? def.icon,
          price:     src.price     ?? def.price,
          currency:  src.currency  ?? def.currency,
          maxGames:  src.maxGames  ?? def.maxGames,
          maxPoints: src.maxPoints ?? def.maxPoints,
          features: { ...def.features, ...(src.features || {}) },
        };
      }
      this.plans = merged;
    },

    toggle(planKey, featKey) {
      this.plans[planKey].features[featKey] = !this.plans[planKey].features[featKey];
    },

    async saveAll() {
      this.saving = true;
      try {
        const db = firebase.database();
        await db.ref('/plans').set(this.plans);
        this.toast = true;
        setTimeout(() => { this.toast = false; }, 3000);
      } catch (e) {
        alert('Erreur lors de la sauvegarde : ' + e.message);
      }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.pa-root {
  min-height: 100vh;
  background: #f0f4f8;
  font-family: system-ui, sans-serif;
  padding-bottom: 60px;
}

/* ── Center states ── */
.pa-center {
  display: flex; align-items: center; justify-content: center;
  min-height: 100vh;
}
.pa-lock-card {
  background: white; border-radius: 16px; padding: 40px;
  text-align: center; box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}
.pa-lock-card h2 { color: #dc2626; margin: 12px 0 8px; }
.pa-lock-card p  { color: #64748b; font-size: 14px; }
.pa-spinner {
  width: 40px; height: 40px;
  border: 4px solid #e2e8f0; border-top-color: #1a73e8;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Header ── */
.pa-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 12px;
  flex-wrap: wrap; gap: 12px;
}
.pa-title { font-size: 22px; font-weight: 800; color: #1e293b; }
.pa-header-actions { display: flex; gap: 10px; align-items: center; }

.pa-btn-preview {
  padding: 10px 18px; background: #f1f5f9; border: 1.5px solid #cbd5e1;
  border-radius: 8px; font-size: 13px; font-weight: 600; color: #475569;
  text-decoration: none; cursor: pointer;
}
.pa-btn-preview:hover { background: #e2e8f0; }

.pa-btn-save {
  padding: 10px 22px; background: #1a73e8; color: white;
  border: none; border-radius: 8px; font-size: 14px; font-weight: 700;
  cursor: pointer;
}
.pa-btn-save:hover:not(:disabled) { background: #1557b0; }
.pa-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Hints ── */
.pa-hint {
  margin: 0 24px 12px; padding: 12px 16px;
  background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px;
  font-size: 13px; color: #92400e;
}
.pa-hint code { background: #fef3c7; padding: 1px 5px; border-radius: 4px; }

.pa-info-box {
  margin: 0 24px 20px; padding: 12px 16px;
  background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px;
  font-size: 13px; color: #1e40af; line-height: 1.5;
}
.pa-info-box code { background: #dbeafe; padding: 1px 5px; border-radius: 4px; font-size: 12px; }

/* ── Grid ── */
.pa-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 0 24px;
}
@media (max-width: 800px) { .pa-grid { grid-template-columns: 1fr; } }

/* ── Plan column ── */
.pa-plan-col {
  background: white; border-radius: 14px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.07);
  overflow: hidden;
}

.pa-plan-header {
  padding: 16px; color: white;
  display: flex; flex-direction: column; gap: 4px;
}
.accent-free    { background: #64748b; }
.accent-starter { background: #1a73e8; }
.accent-pro     { background: #7c3aed; }

.pa-plan-icon-row { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }

.pa-plan-icon-input {
  background: rgba(255,255,255,0.2); border: none; border-radius: 6px;
  color: white; font-size: 22px; padding: 4px 8px; width: 52px; text-align: center;
  flex-shrink: 0;
}
.pa-plan-icon-input:focus { outline: 2px solid rgba(255,255,255,0.5); }

.pa-plan-name-input {
  background: rgba(255,255,255,0.2); border: none; border-radius: 6px;
  color: white; font-size: 18px; font-weight: 800; padding: 6px 10px;
  flex: 1;
}
.pa-plan-name-input::placeholder { color: rgba(255,255,255,0.5); }
.pa-plan-name-input:focus { outline: 2px solid rgba(255,255,255,0.5); }
.pa-plan-key { font-size: 11px; opacity: 0.7; text-transform: uppercase; letter-spacing: 1px; padding-left: 2px; }

/* ── Section ── */
.pa-section { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; }
.pa-section-title {
  font-size: 11px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 10px;
}

/* ── Fields ── */
.pa-field { margin-bottom: 10px; }
.pa-label { display: block; font-size: 12px; color: #64748b; margin-bottom: 4px; font-weight: 600; }
.pa-input {
  width: 80px; padding: 7px 10px; border: 1.5px solid #e2e8f0;
  border-radius: 7px; font-size: 14px; color: #1e293b;
}
.pa-input:focus { outline: none; border-color: #1a73e8; }
.pa-price-wrap { display: flex; align-items: center; gap: 6px; }
.pa-currency { font-size: 18px; color: #94a3b8; }
.pa-input-row { display: flex; align-items: center; gap: 8px; }
.pa-badge-free, .pa-badge-unlimited {
  font-size: 11px; font-weight: 700; padding: 2px 8px;
  border-radius: 20px;
}
.pa-badge-free      { background: #dcfce7; color: #15803d; }
.pa-badge-unlimited { background: #ede9fe; color: #7c3aed; }

/* ── Checkboxes ── */
.pa-checkbox-row {
  display: flex; align-items: center; gap: 10px;
  padding: 7px 0; cursor: pointer;
  border-radius: 6px; transition: background 0.1s;
}
.pa-checkbox-row:hover { background: #f8fafc; margin: 0 -8px; padding: 7px 8px; }

.pa-checkbox {
  width: 22px; height: 22px; border-radius: 6px; flex-shrink: 0;
  border: 2px solid #cbd5e1; display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; color: white; transition: all 0.15s;
}
.pa-checkbox.checked { background: #22c55e; border-color: #22c55e; }

.pa-feat-label { font-size: 13px; color: #334155; font-weight: 500; }

/* ── Toast ── */
.pa-toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  background: #1a73e8; color: white; padding: 12px 28px;
  border-radius: 10px; font-weight: 700; font-size: 14px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15); z-index: 999;
}
</style>
